import { Alert, Button, Link } from '@apollo/orbit';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';

import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useUpgradeContext } from 'src/hooks/useUpgradeContext';
import {
  FastModeRange,
  RangeSelectedFn,
  TimeRangeOption,
} from 'src/lib/config/timeRangeOptions';
import { date } from 'src/lib/date/date';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { DisabledOptionsSection } from './DisabledOptionsSection';
import {
  EnabledOptionsSection,
  FastModeSection,
} from './EnabledOptionsSection';

// date format source: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local#value
export const DATETIME_LOCAL_FORMAT_STR = 'YYYY-MM-DDTHH:mm';

type Props = {
  className?: string;
  onRangeSelected: RangeSelectedFn;
  ranges: ReadonlyArray<TimeRangeOption>;
  range: TimeRangeOption;
  from: Date;
  to: Date;
  maxRangeInDays: number;
  showFastModeRanges?: boolean;
  fastModeRange?: FastModeRange;
};

const handleChange = (
  to: Date,
  from: Date,
  range: TimeRangeOption,
  onRangeSelected: RangeSelectedFn,
  selectedFastModeRange?: FastModeRange,
) => {
  if (range !== 'custom') {
    onRangeSelected(range, undefined, undefined, selectedFastModeRange);
  } else {
    onRangeSelected(range, from.toISOString(), to.toISOString());
  }
};

export const isDisabledProFeature = (
  range: string,
  isRangeRestricted: boolean,
) =>
  isRangeRestricted &&
  ['lastWeek', 'lastMonth', 'lastThreeMonths', 'custom'].includes(range);

export const TimeRangeFilter = ({
  className,
  range,
  ranges,
  from,
  to,
  maxRangeInDays,
  onRangeSelected,
  showFastModeRanges,
  fastModeRange,
}: Props) => {
  const [updatedFrom, setUpdatedFrom] = useState(from);
  const [updatedTo, setUpdatedTo] = useState(to);
  const [fromError, setFromError] = useState<string>();
  const [toError, setToError] = useState<string>();
  const [rangeError, setRangeError] = useState<string>();
  const lastRange = useRef<TimeRangeOption>(range);
  const accountId = useCurrentAccountId()[0] || '';
  const tier = useCurrentPlan({
    accountId,
  }).currentPlan?.tier;
  const { upgradeRoute } = useUpgradeContext();

  useEffect(() => {
    if (lastRange.current !== range) {
      // If the time range option changes (i.e. from lastWeek to lastHour),
      // reset the custom state to match it.
      lastRange.current = range;
      setUpdatedFrom(from);
      setUpdatedTo(to);
    }
  }, [from, range, to]);
  const isRangeRestricted = Boolean(maxRangeInDays) && maxRangeInDays === 1;
  const ctaRanges: TimeRangeOption[] = isRangeRestricted
    ? ['lastWeek', 'lastMonth', 'lastThreeMonths', 'custom']
    : [];
  const allOptions: TimeRangeOption[] = [
    ...ranges,
    ...(isRangeRestricted ? ctaRanges : []),
  ];

  const shouldShowDatePicker = ranges.includes('custom') && range === 'custom';
  const customRangeValid =
    !isNaN(updatedFrom.valueOf()) && !isNaN(updatedTo.valueOf());
  const customRangeDirty =
    from.valueOf() !== updatedFrom.valueOf() ||
    to.valueOf() !== updatedTo.valueOf();
  const maxRange = moment.duration(maxRangeInDays, 'days');
  const disabledOptions: TimeRangeOption[] = [];
  const enabledOptions: TimeRangeOption[] = [];
  allOptions.forEach((option) => {
    if (isDisabledProFeature(option, isRangeRestricted)) {
      disabledOptions.push(option);
    } else enabledOptions.push(option);
  });

  const fastModeRangesEnabled = !isRangeRestricted && showFastModeRanges;

  return (
    <div className={className}>
      <EnabledOptionsSection
        options={enabledOptions}
        handleChange={handleChange}
        onRangeSelected={onRangeSelected}
        isDisabledProFeature={isDisabledProFeature}
        range={range}
        from={from}
        to={to}
        maxRangeInDays={maxRangeInDays}
        fastModeRange={fastModeRange}
      />

      {disabledOptions.length > 0 && (
        <>
          <div className="my-2 flex items-center border-t border-silver">
            <h3 className="my-1 text-sm font-semibold uppercase">
              Unavailable
            </h3>
          </div>
          <DisabledOptionsSection options={disabledOptions} />
        </>
      )}

      {shouldShowDatePicker && (
        <>
          <div className="mt-4 text-sm font-semibold">From</div>
          <input
            data-testid="custom-from-input"
            defaultValue={moment(from).format(DATETIME_LOCAL_FORMAT_STR)}
            onChange={(e) => {
              const newUpdatedFrom = new Date(e.target.value);
              if (!e.target.validity.valid) {
                setFromError(e.target.validationMessage);
                if (!isNaN(newUpdatedFrom.valueOf())) {
                  // only update our react state if the input is a valid date
                  setUpdatedFrom(newUpdatedFrom);
                }
              } else {
                setFromError(undefined);
                try {
                  date.validateDateSelection(
                    newUpdatedFrom,
                    updatedTo,
                    maxRange,
                  );
                  setUpdatedFrom(newUpdatedFrom);
                  setRangeError(undefined);
                } catch (err) {
                  setRangeError((err as Error).message);
                }
              }
            }}
            type="datetime-local"
            className="w-full rounded border border-primary px-2 hover:border-blue"
            min={moment()
              .subtract(maxRangeInDays, 'days')
              .format(DATETIME_LOCAL_FORMAT_STR)}
            max={moment().format(DATETIME_LOCAL_FORMAT_STR)}
          />
          {fromError && (
            <div className="mt-2 leading-tight text-red">{fromError}</div>
          )}
          <div className="mt-2 text-sm font-semibold">To</div>
          <input
            data-testid="custom-to-input"
            defaultValue={moment(updatedTo).format(DATETIME_LOCAL_FORMAT_STR)}
            onChange={(e) => {
              const newUpdatedTo = new Date(e.target.value);
              if (!e.target.validity.valid) {
                setToError(e.target.validationMessage);
                if (!isNaN(newUpdatedTo.valueOf())) {
                  // only update our react state if the input is a valid date
                  setUpdatedTo(newUpdatedTo);
                }
              } else {
                setToError(undefined);
                try {
                  date.validateDateSelection(
                    updatedFrom,
                    newUpdatedTo,
                    maxRange,
                  );
                  setUpdatedTo(newUpdatedTo);
                  setRangeError(undefined);
                } catch (err) {
                  setRangeError((err as Error).message);
                }
              }
            }}
            type="datetime-local"
            className="w-full rounded border border-primary px-2 hover:border-blue"
            min={moment()
              .subtract(maxRangeInDays, 'days')
              .format(DATETIME_LOCAL_FORMAT_STR)}
            max={moment().format(DATETIME_LOCAL_FORMAT_STR)}
          />
          {toError && (
            <div className="mt-2 leading-tight text-red">{toError}</div>
          )}
          {customRangeValid && (
            <div className="mt-2 leading-tight text-secondary">
              Range:{' '}
              {date.printRangeDuration(
                moment.duration(
                  moment(updatedTo)
                    .startOf('minute')
                    .diff(moment(updatedFrom).startOf('minute')),
                ),
              )}
            </div>
          )}
          {rangeError && !fromError && !toError && (
            <div className="mt-2 leading-tight text-red">{rangeError}</div>
          )}
          {customRangeDirty && !fromError && !toError && !rangeError && (
            <Button
              size="sm"
              className="mt-4"
              onClick={() => {
                onRangeSelected(
                  'custom',
                  updatedFrom.toISOString(),
                  updatedTo.toISOString(),
                );
              }}
            >
              Update
            </Button>
          )}
        </>
      )}

      {fastModeRangesEnabled && (
        <FastModeSection
          range={range}
          fastModeRange={fastModeRange}
          handleClick={(option: FastModeRange) => {
            handleChange(to, from, option, onRangeSelected, option);
          }}
        />
      )}

      {tier !== GraphQLTypes.BillingPlanTier.ENTERPRISE && (
        <Alert status="beta" className="mt-2">
          <Link className="mr-1" to={upgradeRoute}>
            Upgrade
          </Link>
          to unlock access to longer data retention
        </Alert>
      )}
    </div>
  );
};
